import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import usePermissions from "hooks/usePermissions";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import { FilterConfig } from "types/Common";
import useSortedProviderTeams from "hooks/useSortedProviderTeams";

const DISPLAY_STATUS = {
  ALL: "",
  BOOKED: "Booked",
  SHIPPING: "In transit",
  DELAYED: "Delayed",
  DELIVERED: "Delivered",
  INCOMPLETE: "Incomplete",
  ARCHIVED: "archived",
};

export default function useShipmentFilters({ status, type }: { status?: string; type: "shipper" | "provider" }) {
  const intl = useIntl();
  const { getPaymentLocationLabel, getProviderTeamLabel } = usePermissions();
  const queryClient = useQueryClient();
  const paymentLocations = useSortedPaymentLocations();
  const providerTeams = useSortedProviderTeams();

  // for client side filtering we don't have payment location id, so we'll filter with string title instead
  const paymentLocationWithTitle = useMemo(
    () =>
      paymentLocations.map((item) => ({
        ...item,
        value: item.label,
      })),
    [paymentLocations],
  );

  // for client side filtering we don't have payment location id, so we'll filter with string title instead
  const providerTeamsWithTitle = useMemo(
    () =>
      providerTeams.map((item) => ({
        ...item,
        value: item.label,
      })),
    [providerTeams],
  );

  const SHIPMENT_DISPLAY_STATUS = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "general.inTransit", defaultMessage: "In transit" }),
        value: "In transit",
      },
      {
        label: intl.formatMessage({ id: "general.incomplete", defaultMessage: "Incomplete" }),
        value: "Incomplete",
      },
      {
        label: intl.formatMessage({ id: "general.delayed", defaultMessage: "Delayed" }),
        value: "Delayed",
      },
      {
        label: intl.formatMessage({ id: "general.archived", defaultMessage: "Archive" }),
        value: "archived",
      },
      {
        label: intl.formatMessage({ id: "general.delivered", defaultMessage: "Delivered" }),
        value: "Delivered",
      },
      {
        label: intl.formatMessage({ id: "general.booked", defaultMessage: "Booked" }),
        value: "Booked",
      },
    ],
    [intl],
  );

  const config: FilterConfig[] = [
    {
      filterType: "multiselect",
      label: intl.formatMessage({ id: "general.displayStatus", defaultMessage: "Display status" }),
      key: "display_status",
      options: SHIPMENT_DISPLAY_STATUS,
    },
    ...(type === "shipper"
      ? [
          {
            filterType: "multiselect",
            label: getPaymentLocationLabel(),
            key: "payment_location",
            isSearchable: true,
            options: status === DISPLAY_STATUS.ARCHIVED ? paymentLocations : paymentLocationWithTitle,
          } as FilterConfig,
        ]
      : []),
    ...(type === "provider"
      ? [
          {
            filterType: "multiselect",
            label: getProviderTeamLabel(),
            key: "assigned_team",
            isSearchable: true,
            options: status === DISPLAY_STATUS.ARCHIVED ? providerTeams : providerTeamsWithTitle,
          } as FilterConfig,
        ]
      : []),
    {
      filterType: "freight_mode",
      label: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" }),
      key: "freight_mode",
      filterValueKey: status === DISPLAY_STATUS.ARCHIVED ? "key" : "translated_value",
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.origin", defaultMessage: "Origin" }),
      key: "origin",
      nestedFilters: [
        ...(type === "shipper"
          ? [
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.requestedPickupDate",
                  defaultMessage: "Request Pickup date",
                }),
                key: "requested_pickup_date",
              },
              {
                filterType: "date",
                label: intl.formatMessage({ id: "general.quotedPickupDate", defaultMessage: "Quoted Pickup date" }),
                key: "quoted_pickup_date",
              },
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.expectedPickupDate",
                  defaultMessage: "Expected Pickup date",
                }),
                key: "expected_pickup_date",
              },
            ]
          : []),
        ...(type === "provider"
          ? [
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.pickupDate",
                  defaultMessage: "Pickup Date",
                }),
                key: "pickup_date",
              },
            ]
          : []),
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.pickupCity", defaultMessage: "Pickup city" }),
          key: "pickup_city",
          ...(status !== DISPLAY_STATUS.ARCHIVED && {
            options: [
              ...new Set(
                queryClient
                  .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                  ?.map((item) => item.pickup_city)
                  ?.filter((item) => item),
              ),
            ],
          }),
        },
        ...(type === "shipper"
          ? [
              {
                filterType: "text",
                label: intl.formatMessage({ id: "quote-request-detail.pickupCompany", defaultMessage: "Pickup Company" }),
                key: "origin_name",
                ...(status !== DISPLAY_STATUS.ARCHIVED && {
                  options: [
                    ...new Set(
                      queryClient
                        .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                        ?.map((item) => item?.origin.origin_name)
                        ?.filter((item) => item),
                    ),
                  ],
                }),
              },
              {
                filterType: "text",
                label: "(Air)port of Loading",
                key: "pol_locode",
                ...(status !== DISPLAY_STATUS.ARCHIVED && {
                  options: [
                    ...new Set(
                      queryClient
                        .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                        ?.map((item) => item.pol_locode)
                        ?.filter((item) => item),
                    ),
                  ],
                }),
              },
            ]
          : []),
      ],
    },
    {
      filterType: "accordion",
      label: intl.formatMessage({ id: "general.destination", defaultMessage: "Destination" }),
      key: "destination",
      nestedFilters: [
        ...(type === "shipper"
          ? [
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.requestedDeliveryDate",
                  defaultMessage: "Requested Delivery date",
                }),
                key: "requested_delivery_date",
              },
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.quotedDeliveryDate",
                  defaultMessage: "Quoted Delivery date",
                }),
                key: "quoted_delivery_date",
              },
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "general.expectedDeliveryDate",
                  defaultMessage: "Expected Delivery date",
                }),
                key: "expected_delivery_date",
              },
            ]
          : []),
        ...(type === "provider"
          ? [
              {
                filterType: "date",
                label: intl.formatMessage({
                  id: "report.DeliveryDate",
                  defaultMessage: "Delivery Date",
                }),
                key: "delivery_date",
              },
            ]
          : []),
        {
          filterType: "text",
          label: intl.formatMessage({ id: "general.deliveryCity", defaultMessage: "Delivery city" }),
          key: "delivery_city",
          ...(status !== DISPLAY_STATUS.ARCHIVED && {
            options: [
              ...new Set(
                queryClient
                  .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                  ?.map((item) => item.stop1_city)
                  ?.filter((item) => item),
              ),
            ],
          }),
        },
        ...(type === "shipper"
          ? [
              {
                filterType: "text",
                label: intl.formatMessage({
                  id: "quote-request-detail.deliveryCompany",
                  defaultMessage: "Delivery Company",
                }),
                key: "destination_name",
                ...(status !== DISPLAY_STATUS.ARCHIVED && {
                  options: [
                    ...new Set(
                      queryClient
                        .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                        ?.map((item) => item?.delivery.destination_name)
                        ?.filter((item) => item),
                    ),
                  ],
                }),
              },
              {
                filterType: "text",
                label: "(Air)port of Destination",
                key: "pod_locode",
                ...(status !== DISPLAY_STATUS.ARCHIVED && {
                  options: [
                    ...new Set(
                      queryClient
                        .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                        ?.map((item) => item.pod_locode)
                        ?.filter((item) => item),
                    ),
                  ],
                }),
              },
            ]
          : []),
      ],
    },
    ...(type === "shipper"
      ? ([
          {
            filterType: "text",
            label: intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created by" }),
            key: "created_by",
            ...(status !== DISPLAY_STATUS.ARCHIVED && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                    ?.map((item) => item?.created_by?.name)
                    ?.filter((item) => item),
                ),
              ],
            }),
          },
          {
            filterType: "text",
            label: intl.formatMessage({ id: "general.provider", defaultMessage: "Provider" }),
            key: "awarded_provider",
            ...(status !== DISPLAY_STATUS.ARCHIVED && {
              options: [
                ...new Set(
                  queryClient
                    .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                    ?.map((item) => item.awarded_provider)
                    ?.filter((item) => item),
                ),
              ],
            }),
          },
        ] as FilterConfig[])
      : []),
    ...(type === "provider"
      ? [
          {
            filterType: "multiselect",
            label: intl.formatMessage({ id: "general.shipper", defaultMessage: "Shipper" }),
            key: "shipper",
            isSearchable: true,
            options: [
              ...new Set(
                queryClient
                  .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
                  ?.map((item) => item.shipper)
                  ?.filter((item) => item),
              ),
            ].map((item) => ({ label: item, value: item })),
          } as FilterConfig,
        ]
      : []),
  ];

  return { config };
}
